import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  ChevronLeft, Upload, FileText, Loader2, CheckCircle2, ClipboardList,
  User, Waves, Brain, Heart, Eye, Shield, MessageCircle, Lightbulb, Dumbbell, TreePine
} from 'lucide-react';

/* ──────────── checkbox-group helper ──────────── */
function CheckGroup({ options, selected, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map(opt => (
        <label key={opt} className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox
            checked={selected.includes(opt)}
            onCheckedChange={checked => {
              onChange(checked ? [...selected, opt] : selected.filter(v => v !== opt));
            }}
            data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/\s/g, '-')}`}
          />
          <span className="text-sm text-slate-700">{opt}</span>
        </label>
      ))}
    </div>
  );
}

/* ──────────── radio-style level selector ──────────── */
function LevelSelect({ value, onChange, testId }) {
  const levels = ['High', 'Moderate', 'Low'];
  return (
    <div className="flex gap-2">
      {levels.map(lvl => (
        <button
          key={lvl}
          type="button"
          onClick={() => onChange(lvl)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
            value === lvl
              ? 'bg-[#1E1B4B] text-white border-[#1E1B4B]'
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
          }`}
          data-testid={`${testId}-${lvl.toLowerCase()}`}
        >
          {lvl}
        </button>
      ))}
    </div>
  );
}

function YesNoSelect({ value, onChange, testId }) {
  return (
    <div className="flex gap-2">
      {['Yes', 'No'].map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-all border ${
            value === opt
              ? 'bg-[#1E1B4B] text-white border-[#1E1B4B]'
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
          }`}
          data-testid={`${testId}-${opt.toLowerCase()}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

/* ──────────── default form state ──────────── */
const EMPTY_FORM = {
  // Basic
  child_name: '', age: '', diagnosis: '',
  // Sensory
  sound_sensitivity: '', visual_sensitivity: '', tactile_sensitivity: '',
  vestibular_sensitivity: '', proprioceptive_needs: [],
  proprioceptive_other: '',
  // Triggers
  common_triggers: '', avoidance_behaviors: '', calming_strategies: '',
  // Communication
  communication_style: [], communication_other: '',
  social_preferences: [], social_other: '',
  reinforcement_preferences: '',
  // Learning
  visual_learner: '', kinesthetic_learner: '', auditory_learner: '',
  prompt_preferences: [], prompt_other: '',
  // Interests
  preferred_activities: '', special_interests: '', reinforcers: '',
  // Motor
  gross_motor_skills: '', fine_motor_skills: '', coordination: '',
  // Emotional
  anxiety_levels: '', fear_of_water: '', confidence_level: '',
  // Parent
  parent_goals: '', parent_observations: '',
  // Environment
  pool_environment: '', equipment_preferences: '',
  routine_structure: [], routine_other: '',
  // Medical
  medical_conditions: '', safety_concerns: '',
  // Instructor
  friend_instructor_consent: '', friend_instructor_notes: '',
  // Additional
  additional_notes: '',
};

export default function IntakeFormPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [child, setChild] = useState(null);
  const [mode, setMode] = useState('choose');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [childRes, intakeRes] = await Promise.all([
          api.get(`/children/${childId}`),
          api.get(`/children/${childId}/intake-profile`),
        ]);
        setChild(childRes.data);
        if (intakeRes.data.intake_profile) {
          setExisting(intakeRes.data);
          const p = intakeRes.data.intake_profile;
          setForm(prev => {
            const updated = { ...prev };
            Object.keys(prev).forEach(k => {
              if (p[k] !== undefined && p[k] !== null) updated[k] = p[k];
            });
            return updated;
          });
        }
      } catch { toast.error('Failed to load child data'); }
      setLoading(false);
    };
    fetchData();
  }, [childId]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.diagnosis.trim()) { toast.error('Please enter a diagnosis or condition'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/children/${childId}/intake-form`, form);
      setExisting({ intake_profile: res.data.profile, intake_method: 'digital_form', intake_uploaded_at: new Date().toISOString() });
      toast.success(res.data.message || 'Intake form submitted!');
      setMode('choose');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to submit form'); }
    setSubmitting(false);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Please select a PDF file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/children/${childId}/intake-pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setExisting({ intake_profile: res.data.profile, intake_method: 'pdf', intake_uploaded_at: new Date().toISOString() });
      toast.success(res.data.message || 'PDF intake form uploaded!');
      setMode('choose');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to upload PDF'); }
    setUploading(false);
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn pb-8" data-testid="intake-form-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)} data-testid="back-btn">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>Intake Form</h1>
          <p className="text-sm text-slate-500">{child?.name} — Big Effort Swim Trial Program</p>
        </div>
      </div>

      {/* Existing banner */}
      {existing?.intake_profile && mode === 'choose' && (
        <Card className="rounded-2xl border-green-200 bg-green-50/50 mb-6" data-testid="existing-intake-banner">
          <CardContent className="p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-800">Intake form already on file</p>
              <p className="text-xs text-green-600">
                {existing.intake_method === 'digital_form' ? 'Submitted digitally' : 'Uploaded as PDF'} on {new Date(existing.intake_uploaded_at).toLocaleDateString()}
              </p>
            </div>
            <Badge className="rounded-full bg-green-100 text-green-700 border-green-300">{existing.intake_profile.diagnosis || 'Completed'}</Badge>
          </CardContent>
        </Card>
      )}

      {/* Mode Chooser */}
      {mode === 'choose' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="intake-mode-chooser">
          <Card className="rounded-3xl border-2 border-slate-200 hover:border-[#22D3EE] cursor-pointer transition-all hover:shadow-lg group" onClick={() => setMode('digital')} data-testid="choose-digital-form">
            <CardContent className="p-8 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E1B4B] to-[#312E81] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <ClipboardList className="w-8 h-8 text-[#C4F017]" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito' }}>Fill Digital Form</h3>
              <p className="text-sm text-slate-500">Complete the intake form with easy checkboxes and fields</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border-2 border-slate-200 hover:border-[#22D3EE] cursor-pointer transition-all hover:shadow-lg group" onClick={() => setMode('pdf')} data-testid="choose-pdf-upload">
            <CardContent className="p-8 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0E7490] to-[#22D3EE] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Upload className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2" style={{ fontFamily: 'Nunito' }}>Upload PDF</h3>
              <p className="text-sm text-slate-500">Upload an existing intake form PDF to be AI-processed</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* PDF Upload */}
      {mode === 'pdf' && (
        <Card className="rounded-3xl" data-testid="pdf-upload-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}><Upload className="w-5 h-5 text-[#22D3EE]" /> Upload PDF Intake Form</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setMode('choose')} className="text-slate-500">Back</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-slate-200 rounded-2xl p-12 flex flex-col items-center text-center">
              <FileText className="w-16 h-16 text-slate-300 mb-4" />
              <p className="text-slate-600 font-semibold mb-1">Drop a PDF here or click to browse</p>
              <p className="text-xs text-slate-400 mb-4">Max file size: 10MB. AI will extract and structure the data.</p>
              <label className="cursor-pointer">
                <input type="file" accept=".pdf" className="hidden" onChange={handlePdfUpload} data-testid="pdf-file-input" />
                <Button asChild disabled={uploading} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2">
                  <span>{uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><Upload className="w-4 h-4" /> Select PDF</>}</span>
                </Button>
              </label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Digital Form */}
      {mode === 'digital' && (
        <div className="space-y-4" data-testid="digital-form-section">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-slate-500">Fill in as much detail as possible to help the instructor</p>
            <Button variant="ghost" size="sm" onClick={() => setMode('choose')} className="text-slate-500">Back</Button>
          </div>

          {/* ─── 1. CHILD INFO ─── */}
          <Card className="rounded-2xl" data-testid="section-child-info">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><User className="w-4 h-4 text-[#0E7490]" /> Child Information</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-slate-600 mb-1 block">Age</Label>
                  <Input className="rounded-xl" placeholder="e.g. 7 years old" value={form.age} onChange={e => set('age', e.target.value)} data-testid="field-age" />
                </div>
                <div>
                  <Label className="text-sm text-slate-600 mb-1 block">Diagnosis / Condition *</Label>
                  <Input className="rounded-xl" placeholder="e.g. Autism Spectrum Disorder (Level 2)" value={form.diagnosis} onChange={e => set('diagnosis', e.target.value)} data-testid="field-diagnosis" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ─── 2. SENSORY PROFILE ─── */}
          <Card className="rounded-2xl" data-testid="section-sensory">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Eye className="w-4 h-4 text-[#0E7490]" /> Sensory Profile</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Sound Sensitivity <span className="text-slate-400 text-xs">(e.g., dislikes loud noises, prefers quiet)</span></Label>
                <LevelSelect value={form.sound_sensitivity} onChange={v => set('sound_sensitivity', v)} testId="sound-sens" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Visual Sensitivity <span className="text-slate-400 text-xs">(e.g., sensitive to bright lights, reflections)</span></Label>
                <LevelSelect value={form.visual_sensitivity} onChange={v => set('visual_sensitivity', v)} testId="visual-sens" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Tactile Sensitivity <span className="text-slate-400 text-xs">(e.g., dislikes cold water, certain textures)</span></Label>
                <LevelSelect value={form.tactile_sensitivity} onChange={v => set('tactile_sensitivity', v)} testId="tactile-sens" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Vestibular Sensitivity <span className="text-slate-400 text-xs">(e.g., dislikes floating, being submerged)</span></Label>
                <LevelSelect value={form.vestibular_sensitivity} onChange={v => set('vestibular_sensitivity', v)} testId="vestibular-sens" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Proprioceptive Needs</Label>
                <CheckGroup
                  options={['Seeks deep pressure', 'Prefers resistance']}
                  selected={form.proprioceptive_needs}
                  onChange={v => set('proprioceptive_needs', v)}
                  testIdPrefix="proprio"
                />
                <Input className="rounded-xl mt-2" placeholder="Other proprioceptive needs..." value={form.proprioceptive_other} onChange={e => set('proprioceptive_other', e.target.value)} data-testid="proprio-other" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 3. BEHAVIORAL TRIGGERS ─── */}
          <Card className="rounded-2xl" data-testid="section-triggers">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Shield className="w-4 h-4 text-[#0E7490]" /> Behavioral Triggers</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Common Triggers <span className="text-slate-400 text-xs">(e.g., sudden changes, loud noises, unfamiliar people)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.common_triggers} onChange={e => set('common_triggers', e.target.value)} data-testid="field-common-triggers" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Avoidance Behaviors <span className="text-slate-400 text-xs">(e.g., avoids putting face in water, avoids deep water)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.avoidance_behaviors} onChange={e => set('avoidance_behaviors', e.target.value)} data-testid="field-avoidance-behaviors" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Calming Strategies <span className="text-slate-400 text-xs">(e.g., deep breathing, counting, holding a favorite object)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.calming_strategies} onChange={e => set('calming_strategies', e.target.value)} data-testid="field-calming-strategies" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 4. COMMUNICATION & SOCIAL ─── */}
          <Card className="rounded-2xl" data-testid="section-communication">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><MessageCircle className="w-4 h-4 text-[#0E7490]" /> Communication & Social</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Communication Style</Label>
                <CheckGroup
                  options={['Verbal', 'Non-verbal', 'Uses AAC device']}
                  selected={form.communication_style}
                  onChange={v => set('communication_style', v)}
                  testIdPrefix="comm"
                />
                <Input className="rounded-xl mt-2" placeholder="Other..." value={form.communication_other} onChange={e => set('communication_other', e.target.value)} data-testid="comm-other" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Social Preferences</Label>
                <CheckGroup
                  options={['Prefers one-on-one', 'Prefers group', 'Prefers familiar person (e.g., parent)']}
                  selected={form.social_preferences}
                  onChange={v => set('social_preferences', v)}
                  testIdPrefix="social"
                />
                <Input className="rounded-xl mt-2" placeholder="Other..." value={form.social_other} onChange={e => set('social_other', e.target.value)} data-testid="social-other" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Reinforcement Preferences <span className="text-slate-400 text-xs">(e.g., verbal praise, high-fives, stickers, access to preferred toy)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.reinforcement_preferences} onChange={e => set('reinforcement_preferences', e.target.value)} data-testid="field-reinforcement" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 5. LEARNING STYLE ─── */}
          <Card className="rounded-2xl" data-testid="section-learning">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Lightbulb className="w-4 h-4 text-[#0E7490]" /> Learning Style</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Visual Learner <span className="text-slate-400 text-xs">(responds well to pictures, videos)</span></Label>
                <YesNoSelect value={form.visual_learner} onChange={v => set('visual_learner', v)} testId="visual-learn" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Kinesthetic Learner <span className="text-slate-400 text-xs">(learns best through hands-on activities)</span></Label>
                <YesNoSelect value={form.kinesthetic_learner} onChange={v => set('kinesthetic_learner', v)} testId="kinesthetic-learn" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Auditory Learner <span className="text-slate-400 text-xs">(responds well to verbal instructions or songs)</span></Label>
                <YesNoSelect value={form.auditory_learner} onChange={v => set('auditory_learner', v)} testId="auditory-learn" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Prompt Preferences</Label>
                <CheckGroup
                  options={['Physical', 'Verbal', 'Modeling', 'Gestural', 'Visual']}
                  selected={form.prompt_preferences}
                  onChange={v => set('prompt_preferences', v)}
                  testIdPrefix="prompt"
                />
                <Input className="rounded-xl mt-2" placeholder="Other..." value={form.prompt_other} onChange={e => set('prompt_other', e.target.value)} data-testid="prompt-other" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 6. INTERESTS & REINFORCERS ─── */}
          <Card className="rounded-2xl" data-testid="section-interests">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Lightbulb className="w-4 h-4 text-[#0E7490]" /> Interests & Reinforcers</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Preferred Activities <span className="text-slate-400 text-xs">(e.g., playing with water toys, splashing, floating)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.preferred_activities} onChange={e => set('preferred_activities', e.target.value)} data-testid="field-preferred-activities" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Special Interests <span className="text-slate-400 text-xs">(e.g., dinosaurs, trains, animals)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.special_interests} onChange={e => set('special_interests', e.target.value)} data-testid="field-special-interests" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Reinforcers <span className="text-slate-400 text-xs">(e.g., stickers, small toys, access to favorite activity)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.reinforcers} onChange={e => set('reinforcers', e.target.value)} data-testid="field-reinforcers" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 7. MOTOR SKILLS ─── */}
          <Card className="rounded-2xl" data-testid="section-motor">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Dumbbell className="w-4 h-4 text-[#0E7490]" /> Motor Skills</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Gross Motor Skills <span className="text-slate-400 text-xs">(e.g., can kick legs, struggles with arm movements)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.gross_motor_skills} onChange={e => set('gross_motor_skills', e.target.value)} data-testid="field-gross-motor" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Fine Motor Skills <span className="text-slate-400 text-xs">(e.g., can hold pool noodle, needs assistance with kickboards)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.fine_motor_skills} onChange={e => set('fine_motor_skills', e.target.value)} data-testid="field-fine-motor" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Coordination <span className="text-slate-400 text-xs">(e.g., struggles with combining arm and leg movements)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.coordination} onChange={e => set('coordination', e.target.value)} data-testid="field-coordination" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 8. EMOTIONAL FACTORS ─── */}
          <Card className="rounded-2xl" data-testid="section-emotional">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Heart className="w-4 h-4 text-[#0E7490]" /> Emotional Factors</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Anxiety Levels <span className="text-slate-400 text-xs">(e.g., anxious in new environments)</span></Label>
                <LevelSelect value={form.anxiety_levels} onChange={v => set('anxiety_levels', v)} testId="anxiety" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Fear of Water <span className="text-slate-400 text-xs">(e.g., fear of submerging head, fear of deep water)</span></Label>
                <YesNoSelect value={form.fear_of_water} onChange={v => set('fear_of_water', v)} testId="fear-water" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Confidence Level <span className="text-slate-400 text-xs">(e.g., needs small steps to build confidence)</span></Label>
                <LevelSelect value={form.confidence_level} onChange={v => set('confidence_level', v)} testId="confidence" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 9. PARENT INPUT ─── */}
          <Card className="rounded-2xl" data-testid="section-parent">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Brain className="w-4 h-4 text-[#0E7490]" /> Parent Input</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Parent Goals <span className="text-slate-400 text-xs">(e.g., safety skills, social interaction, physical fitness)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.parent_goals} onChange={e => set('parent_goals', e.target.value)} data-testid="field-parent-goals" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Parent Observations <span className="text-slate-400 text-xs">(e.g., enjoys water play but avoids deep water)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.parent_observations} onChange={e => set('parent_observations', e.target.value)} data-testid="field-parent-observations" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 10. ENVIRONMENT & EQUIPMENT ─── */}
          <Card className="rounded-2xl" data-testid="section-environment">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><TreePine className="w-4 h-4 text-[#0E7490]" /> Environment & Equipment</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Pool Environment <span className="text-slate-400 text-xs">(e.g., quieter time slot, warm water, dimmed lighting)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.pool_environment} onChange={e => set('pool_environment', e.target.value)} data-testid="field-pool-environment" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Equipment Preferences <span className="text-slate-400 text-xs">(e.g., blue pool noodles, dinosaur-themed toys)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.equipment_preferences} onChange={e => set('equipment_preferences', e.target.value)} data-testid="field-equipment" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Routine & Structure</Label>
                <CheckGroup
                  options={['Needs predictable routine', 'Benefits from visual schedule']}
                  selected={form.routine_structure}
                  onChange={v => set('routine_structure', v)}
                  testIdPrefix="routine"
                />
                <Input className="rounded-xl mt-2" placeholder="Other..." value={form.routine_other} onChange={e => set('routine_other', e.target.value)} data-testid="routine-other" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 11. MEDICAL & SAFETY ─── */}
          <Card className="rounded-2xl" data-testid="section-medical">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><Shield className="w-4 h-4 text-red-500" /> Medical & Safety</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Medical Conditions <span className="text-slate-400 text-xs">(e.g., seizures, asthma, allergies)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.medical_conditions} onChange={e => set('medical_conditions', e.target.value)} data-testid="field-medical" />
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-1 block">Safety Concerns <span className="text-slate-400 text-xs">(e.g., wandering, difficulty following safety instructions)</span></Label>
                <Textarea className="rounded-xl" rows={2} value={form.safety_concerns} onChange={e => set('safety_concerns', e.target.value)} data-testid="field-safety" />
              </div>
            </CardContent>
          </Card>

          {/* ─── 12. ADDITIONAL NOTES ─── */}
          <Card className="rounded-2xl" data-testid="section-additional">
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-slate-700"><FileText className="w-4 h-4 text-[#0E7490]" /> Additional Notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea className="rounded-xl" rows={3} placeholder="Any additional information the instructor should know..." value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} data-testid="field-additional-notes" />
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full h-14 rounded-2xl bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold text-lg gap-2 shadow-lg"
            data-testid="submit-intake-btn"
          >
            {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : <><CheckCircle2 className="w-5 h-5" /> Submit Intake Form</>}
          </Button>
        </div>
      )}
    </div>
  );
}
